// ================================================================
// US States Constants and Utilities
// Following established pattern for constants with helper functions
// ================================================================

import { US_STATES as SHARED_US_STATES } from '@cheervue/us-states';

/**
 * US States data for state dropdowns and form selections.
 *
 * Derived from `@cheervue/us-states` so the signup form and every address form in here
 * read one list. The SHAPE is preserved exactly as it has always been, and a test pins
 * it: an empty "Select state" option first, then the states alphabetically, then
 * District of Columbia LAST. That trailing DC is the one departure from the shared
 * list's alphabetical order, kept because reordering an option would silently change
 * the seven address dropdowns that render it.
 */
export const US_STATES = [
  { value: "", label: "Select state" },
  ...SHARED_US_STATES.filter((state) => state.value !== "DC"),
  { value: "DC", label: "District of Columbia" }
];

/**
 * State abbreviation to full name mapping.
 *
 * Built from the same list, so a state can never appear in the dropdown while being
 * missing here — the two were hand-maintained copies until now.
 */
export const STATE_NAMES: Record<string, string> = Object.fromEntries(
  SHARED_US_STATES.map((state) => [state.value, state.label])
);

/**
 * Helper functions for state management
 */
export const StateUtils = {
  /**
   * Get full state name from abbreviation
   */
  getStateName: (abbreviation: string): string => {
    return STATE_NAMES[abbreviation] || abbreviation;
  },

  /**
   * Find state by abbreviation
   */
  findStateByAbbreviation: (abbreviation: string) => {
    return US_STATES.find(state => state.value === abbreviation);
  },

  /**
   * Get all state options for dropdowns (excluding empty option)
   */
  getStateOptions: () => {
    return US_STATES.filter(state => state.value !== "");
  },

  /**
   * Validate state abbreviation
   */
  isValidState: (abbreviation: string): boolean => {
    return abbreviation !== "" && abbreviation in STATE_NAMES;
  }
}; 